import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import "./header.scss";
import {
  ContextMenuType,
  NavBar,
  SideBar,
  SideBarItem,
  SideBarNode,
} from "@pickit/pickit-components";
import { CountryCodeType, PickitCountryCodeType } from "reducers/types/session";
import { actions as sessionActions, sessionSelector } from "reducers/session";
import { HeaderProps } from "./types";
import { useHistory } from "react-router-dom";
import { AppDispatch, RootState } from "store";
import { getValue } from "utils/utils";
import { actions as loginActions } from "reducers/login";
import "./i18n";
import { useTranslation } from "react-i18next";
import { dirtyAlert } from "utils/alerts";
import { NotificationStateType } from "reducers/types/notification";
import { actions as notificationActions } from "reducers/notification";
import { COUNTRIES, VERSION } from "utils/constants";
import { actions as slotsActions, slotSelector } from "reducers/slots";
import { SlotsTabs } from "reducers/types/slots";
import { actions as pickersActions, pickersSelector } from "reducers/pickers";
import { pickerTabs } from "pages/pickers/types";

const getEmailFromToken = (token: string): string => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const knownFields = ["email", "mail", "username", "user_name", "preferred_username", "sub"];
    for (const field of knownFields) {
      if (payload[field] && String(payload[field]).includes("@")) return String(payload[field]);
    }
    const emailValue = Object.values(payload).find(
      (value) => typeof value === "string" && value.includes("@")
    );
    return emailValue ? String(emailValue) : "";
  } catch {
    return "";
  }
};

const Header = ({
  countryCode,
  dirty,
  setCountryCode,
  logout,
  showNotification,
  actualPageSlot,
  actualPagePickers,
}: HeaderProps) => {
  const history = useHistory();
  const tokens = getValue("tokens", true);
  const { t } = useTranslation("header");
  const email = countryCode && tokens?.[countryCode] ? getEmailFromToken(tokens[countryCode]) : "";
  const [, toggleFlags] = useState<boolean>(false);
  const [, toggleMenu] = useState<boolean>(false);

  const globalClose = useCallback(
    (e) => {
      e.stopPropagation();
      toggleFlags(false);
      toggleMenu(false);
      window.removeEventListener("click", globalClose);
    },
    [toggleFlags]
  );

  const changeCountry = (countryCode: CountryCodeType) => {
    if (!tokens) {
      setCountryCode(countryCode);
      history.push(`/login`);
    } else {
      const hasToken = Object.entries(tokens).some((token) => {
        const [key, value] = token as [CountryCodeType, string];
        if (countryCode === key && value) return true;
        return false;
      });
      if (hasToken) history.push(`/${countryCode}`);
      else {
        setCountryCode(countryCode);
        history.push(`/login`);
      }
    }
  };

  const handleChangeCountry = (countryCode: CountryCodeType) => {
    if (dirty && showNotification) {
      showNotification(
        dirtyAlert({ onCancel: () => changeCountry(countryCode) })
      );
    } else {
      changeCountry(countryCode);
    }
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("click", globalClose);
    };
  }, [globalClose]);

  const handleLogout = () => {
    if (dirty && showNotification) {
      showNotification(dirtyAlert({ onCancel: logout }));
    } else {
      logout();
    }
  };

  const handleChangePage = (page: string, queryParams?: string) => {
    const queryUrl = new URLSearchParams(queryParams);
    const path = queryUrl
      ? `/${countryCode}/${page}?${queryUrl}`
      : `/${countryCode}/${page}`;
    if (dirty && showNotification) {
      showNotification(
        dirtyAlert({
          onCancel: () => history.push(path),
        })
      );
    } else {
      history.push(path);
    }
  };

  const logoOnClick = () => {
    if (dirty && showNotification) {
      showNotification(
        dirtyAlert({
          onCancel: () => handleChangePage(""),
        })
      );
    } else handleChangePage("");
  };

  const contextMenu: ContextMenuType = {
    ...(email && { userData: { label: email } }),
    items: [
      {
        iconName: "logout",
        label: t("logout"),
        action: handleLogout,
        id: "logout",
      },
    ],
  };

  const queryParams = (value: string) => {
    return new URLSearchParams({
      tab: value,
    }).toString();
  };

  return (
    <>
      <NavBar
        countryCode={countryCode}
        logoOnClick={logoOnClick}
        testId="header"
        setCountryCode={
          handleChangeCountry as (countryCode: PickitCountryCodeType) => void
        }
        countries={COUNTRIES as any}
        contextMenu={contextMenu}
        version={VERSION}
        keepOpen
      >
        <SideBar history={history}>
          <SideBarNode
            iconName="chart-square-bar"
            label={t("reports")}
            opened={true}
            testId="reports-node"
          >
            <SideBarItem
              action={() => handleChangePage("dashboard")}
              label={t("dashboard")}
              path={`/${countryCode}/dashboard`}
              testId="menu-item-dashboard"
            />
            <SideBarItem
              action={() =>
                handleChangePage(
                  "pickers",
                  queryParams(actualPagePickers as string)
                )
              }
              label={t("pickers")}
              path={`/${countryCode}/pickers`}
              testId="menu-item-pickers"
            />
            <SideBarItem
              action={() => handleChangePage("transaction")}
              label={t("transactions")}
              path={`/${countryCode}/transaction`}
              testId="menu-item-transactions"
            />
          </SideBarNode>
          <SideBarNode
            iconName="clipboard-list"
            label={t("administration")}
            testId="administration-node"
          >
            <SideBarItem
              action={() => handleChangePage("presettlements")}
              label={t("preliquidations")}
              path={`/${countryCode}/presettlements`}
              testId="menu-item-presettlements"
            />
            {countryCode === "AR" && (
            <SideBarItem
              action={() => handleChangePage("governmental-entity")}
              label={t("governmentalEntity")}
              path={`/${countryCode}/governmental-entity`}
              testId="menu-item-governmental-entity"
            />
            )}
          </SideBarNode>
          <SideBarNode iconName="cog" label={t("config")} testId="config-node">
            <SideBarItem
              action={() =>
                handleChangePage("polygons", queryParams("general"))
              }
              label={t("polygons")}
              path={`/${countryCode}/polygons`}
              testId="menu-item-polygons"
            />
            <SideBarItem
              action={() =>
                handleChangePage("slots", queryParams(actualPageSlot as string))
              }
              label={t("slots")}
              path={`/${countryCode}/slots`}
              testId="menu-item-slots"
            />
            <SideBarItem
              action={() => handleChangePage("terms-and-conditions")}
              label={t("termsAndConditions")}
              path={`/${countryCode}/terms-and-conditions`}
              testId="menu-item-terms-and-conditions"
            />
          </SideBarNode>
        </SideBar>
      </NavBar>
    </>
  );
};
const mapStateToProps = (state: RootState) => ({
  countryCode: sessionSelector(state).countryCode,
  dirty: sessionSelector(state).dirtyForm,
  actualPageSlot: slotSelector(state).actualPage,
  actualPagePickers: pickersSelector(state).actualPage,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setCountryCode: (countryCode: CountryCodeType) => {
    dispatch(sessionActions.setCountryCode(countryCode));
  },
  logout: () => {
    dispatch(loginActions.logout());
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  setActualPageSlots: (page: SlotsTabs) => {
    dispatch(slotsActions.setActualPage(page));
  },
  setActualPagePickers: (page: pickerTabs) => {
    dispatch(pickersActions.setActualPage(page));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
